import React, { useCallback, useState, useEffect } from "react"
import { Box, Collapse, Flex } from "@chakra-ui/react"
import { graphql, navigate, useStaticQuery } from "gatsby"
import SearchResultsForm from "@app/components/Search/Results/SearchResultsForm"
import SearchSuggestedTerms from "@app/components/Search/Results/SearchSuggestedTerms"
import SearchResultsFeaturedProducts from "@app/components/Search/Results/SearchResultsFeaturedProducts"

import { useConfigContext } from "@app/providers/config"
import { useCore } from "@app/hooks/useCore"
import { useAnalytics } from "@app/hooks/useAnalytics"

import { RemoveScroll } from "react-remove-scroll"
import { useLocation } from "@reach/router"

const SearchForm: React.FC = () => {
  const { trackSearch } = useAnalytics()
  const { search } = useStaticQuery(graphql`
    query HeaderSearch {
      search: sanityPageSearch {
        url
        title
        _type
        inputPlaceholder
        suggestedSearchTerms {
          ... on SanityCollection {
            ...CollectionLinkFragment
          }
          ... on SanityLink {
            ...LinkFragment
          }
        }
        featuredProducts {
          shopify {
            handle
          }
        }
        metadata {
          ...MetadataFragment
        }
      }
    }
  `)
  const {
    helpers: { getUrlParameter },
  } = useCore()
  const {
    store,
    settings: { routes },
  } = useConfigContext()
  const [query, setQuery] = useState(getUrlParameter("q") || "")
  const [redirects, setRedirects] = useState([])
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const location = useLocation()

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetch(store?.searchConfig)
      const json = await data.json()
      setRedirects(json?.body?.redirects || [])
    }
    fetchData().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSearch = useCallback(() => {
    const trimmedValue = query.trim()
    // Prevent empty search submissions
    if (!trimmedValue) {
      return
    }

    trackSearch(trimmedValue)

    document.activeElement?.blur()
    document.getElementById("search-field")?.blur()

    const redirect = redirects.find(({ query }) => query === trimmedValue?.toLowerCase())
    if (redirect) {
      navigate(redirect.url)
    } else {
      window.sessionStorage.setItem("ab.shopify.search_intent", trimmedValue)
      navigate(`${routes.SEARCH}?q=${trimmedValue}`, { state: { disableTransition: true } })
    }
  }, [query, redirects, routes.SEARCH, trackSearch])

  const onClear = useCallback(() => {
    setQuery("")
    navigate(routes.SEARCH, { state: { disableTransition: true } })
  }, [routes.SEARCH])

  return (
    <RemoveScroll removeScrollBar={false} allowPinchZoom={false} enabled={isOpen} forwardProps>
      <Flex bgColor="grey.lightest" height={isOpen ? "100vh" : "auto"} flexDirection="column" overflowY="hidden">
        <Box bgColor="primary.white" paddingX={5} paddingTop={1} paddingBottom={3} flexShrink={0}>
          <SearchResultsForm
            id="results-search-form"
            query={query}
            inputPlaceholder={search?.inputPlaceholder}
            handleChange={setQuery}
            handleSearch={onSearch}
            handleClear={onClear}
            handleOpen={(value: boolean) => setIsOpen(value)}
            isOpen={isOpen}
            isHeaderSearch={true}
          />
        </Box>
        <Box height={isOpen ? "calc(100vh - 200px)" : "auto"} overflowY="auto">
          <Collapse in={isOpen} animateOpacity>
            <Flex flexDirection="column" transition="all 0.2s ease">
              <SearchSuggestedTerms suggestedSearchTerms={search?.suggestedSearchTerms} />
              <SearchResultsFeaturedProducts products={search?.featuredProducts} />
            </Flex>
          </Collapse>
        </Box>
      </Flex>
    </RemoveScroll>
  )
}

export default React.memo(SearchForm)
